/**
 * Follow-up agent — the first agent that does *real* work, not simulation.
 *
 * Everything else in the agent layer either (a) waits for a human to type a
 * message (the chat flow) or (b) narrates believable-but-fake progress (the
 * scheduler / ticket autopilot). This module is different: it reads the *real*
 * prospect data, finds the deals that are drifting (an overdue follow-up, or an
 * open deal with no next step at all), and proposes a *real* `schedule_follow_up`
 * write for each one into the approvals inbox.
 *
 * It performs no write itself — it only proposes. The operator approves in the
 * Dashboard approvals inbox, and `resolveApproval` runs the action through
 * `runActionWithUndo`, which genuinely sets the prospect's `followUpDate` +
 * `nextStep` and logs an activity entry. So the loop is honest end-to-end:
 *
 *   real data  →  real proposal  →  operator approval  →  real store write
 *
 * No network, no API key, no worker. It works against localStorage today and
 * against Firestore the moment `VITE_USE_FIRESTORE` flips — the db adapter the
 * executor calls is the same one the humans use.
 */

import { scanSystem } from './systemScanner';
import { queueApproval, listApprovals } from './approvals';
import type { Person } from '../db/types';
import type { ScheduleFollowUpAction } from './types';

/** Scanner finding categories this agent knows how to act on. */
const ACTIONABLE_CATEGORIES = new Set(['Overdue follow-up', 'No next step']);

export interface ProposeFollowUpsResult {
  /** How many new follow-up proposals were queued for approval. */
  proposed: number;
  /** Prospects that already had a pending follow-up proposal (skipped). */
  skipped: number;
}

/**
 * Build the per-finding note shown on the approval card and stored as the
 * prospect's `nextStep` once approved.
 */
function noteFor(category: string): string {
  return category === 'Overdue follow-up'
    ? 'Re-engage — the scheduled follow-up has lapsed.'
    : 'Set a next step so this open deal stops drifting.';
}

/**
 * Scan the prospects in `people`, and queue a `schedule_follow_up` approval for
 * each drifting one. De-dupes against pending approvals so re-running the agent
 * doesn't pile duplicate proposals for the same prospect. Returns how many were
 * proposed vs. skipped. The `followUpDate` is intentionally omitted so the
 * executor defaults it to +3 days at approval time.
 */
export async function proposeFollowUps(people: Person[]): Promise<ProposeFollowUpsResult> {
  // Only the prospect buckets need `people`; the other collections aren't
  // consulted for follow-up findings, so empty arrays keep the scan focused.
  const { findings } = scanSystem({ tickets: [], tasks: [], people, accounts: [] });
  const drifting = findings.filter(
    (f) => f.source === 'prospect' && ACTIONABLE_CATEGORIES.has(f.category),
  );

  // Don't propose a second follow-up for a prospect that already has one
  // waiting in the inbox.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'schedule_follow_up')
      .map((a) => (a.action as ScheduleFollowUpAction).personId),
  );

  let proposed = 0;
  let skipped = 0;
  for (const f of drifting) {
    if (alreadyQueued.has(f.entityId)) {
      skipped++;
      continue;
    }
    const person = people.find((p) => p.id === f.entityId);
    if (!person) {
      skipped++;
      continue;
    }
    const action: ScheduleFollowUpAction = {
      kind: 'schedule_follow_up',
      personId: person.id,
      personName: person.name,
      note: noteFor(f.category),
    };
    // Evidence-rich proposal: the rationale is the next-step note, the
    // evidence is the scanner's real finding (e.g. "Follow-up 21 days
    // overdue") — so the inbox's "Why?" section cites live data.
    await queueApproval(action, 'scheduler', f.detail, {
      rationale: noteFor(f.category),
      evidence: [f.detail],
    });
    // Guard against duplicate findings for the same prospect within one run.
    alreadyQueued.add(f.entityId);
    proposed++;
  }

  return { proposed, skipped };
}
